import LayoutHR from "layouts/layout-hr";
import React, { useState, useMemo } from "react";
import { Dropdown } from "react-bootstrap";
import { useCanvasHandler } from "hooks/useCanvasHandler";
import Swal from "sweetalert2";
import CreateIdcardPage from "./components/create-idcard-page";
import { getDevelopmentBanner, DEVELOPMENT_STATUS } from "utils/devNotice";

const IdcardPage = () => {
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  // Dummy data for ID cards
  const dummyIdCardData = [
    {
      id: 1,
      photo:
        "https://m.media-amazon.com/images/S/pv-target-images/16627900db04b76fae3b64266ca161511422059cd24062fb5d900971003a0b70._SX1080_FMjpg_.jpg",
      name: "John Doe",
      qrCode: "QR123456789",
      design: "Standard Blue",
      created_at: "2025-01-15",
    },
    {
      id: 2,
      photo:
        "https://m.media-amazon.com/images/S/pv-target-images/16627900db04b76fae3b64266ca161511422059cd24062fb5d900971003a0b70._SX1080_FMjpg_.jpg",
      name: "Jane Smith",
      qrCode: "QR987654321",
      design: "Premium Gold",
      created_at: "2025-01-16",
    },
    {
      id: 3,
      photo:
        "https://m.media-amazon.com/images/S/pv-target-images/16627900db04b76fae3b64266ca161511422059cd24062fb5d900971003a0b70._SX1080_FMjpg_.jpg",
      name: "Bob Johnson",
      qrCode: "QR456789123",
      design: "Classic Black",
      created_at: "2025-01-17",
    },
    {
      id: 4,
      photo:
        "https://m.media-amazon.com/images/S/pv-target-images/16627900db04b76fae3b64266ca161511422059cd24062fb5d900971003a0b70._SX1080_FMjpg_.jpg",
      name: "Alice Wilson",
      qrCode: "QR789123456",
      design: "Modern White",
      created_at: "2025-01-18",
    },
    {
      id: 5,
      photo:
        "https://m.media-amazon.com/images/S/pv-target-images/16627900db04b76fae3b64266ca161511422059cd24062fb5d900971003a0b70._SX1080_FMjpg_.jpg",
      name: "Charlie Brown",
      qrCode: "QR321654987",
      design: "Standard Blue",
      created_at: "2025-01-19",
    },
  ];

  // Filter and paginate data
  const filteredData = useMemo(() => {
    const filtered = dummyIdCardData.filter(
      (item) =>
        item.name.toLowerCase().includes(search.toLowerCase()) ||
        item.qrCode.toLowerCase().includes(search.toLowerCase()) ||
        item.design.toLowerCase().includes(search.toLowerCase())
    );

    const startIndex = (currentPage - 1) * perPage;
    const endIndex = startIndex + perPage;

    return {
      data: filtered.slice(startIndex, endIndex),
      pagination: {
        current_page: currentPage,
        last_page: Math.ceil(filtered.length / perPage),
        per_page: perPage,
        total: filtered.length,
        from: startIndex + 1,
        to: Math.min(endIndex, filtered.length),
        prev_page_url: currentPage > 1 ? true : null,
        next_page_url: endIndex < filtered.length ? true : null,
      },
    };
  }, [search, currentPage, perPage]);

  const idCardData = filteredData;
  const loading = false;
  // const error = null;

  const {
    // handleSuccess,
    handleError,
    showCanvasHandler,
    handleSuccessToast,
    // handleLoadingThenSuccessToast,
  } = useCanvasHandler();

  const handleCreate = (e) => {
    e.preventDefault();
    showCanvasHandler(
      "Create ID Card",
      <CreateIdcardPage
        onSuccess={() => {
          handleSuccessToast(true, "ID card created successfully (Demo Mode)");
        }}
        onError={handleError}
      />
    );
  };

  const handleEdit = (e, data) => {
    e.preventDefault();
    showCanvasHandler(
      "Update ID Card",
      <CreateIdcardPage
        initialData={data}
        onSuccess={() => {
          handleSuccessToast(true, "ID card updated successfully (Demo Mode)");
        }}
        onError={handleError}
      />
    );
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      handleSuccessToast(true, "ID card deleted successfully (Demo Mode)");
    }
  };

  const handlePrint = (id) => {
    // Handle print action
    handleSuccessToast(
      true,
      "Print functionality will be available soon (Demo Mode)"
    );
  };

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      setSearch(e.target.value);
      setCurrentPage(1); // Reset to first page on search
    }
  };

  return (
    <LayoutHR>
      {getDevelopmentBanner()}
      <div className="container-xl">
        {/* Page Header */}
        <div className="page-header d-print-none">
          <div className="row align-items-center">
            <div className="col">
              <h2 className="page-title">ID Card Management</h2>
              <div className="text-muted mt-1">
                Manage employee ID cards and access
              </div>
            </div>
            <div className="col-auto">
              <button className="btn btn-primary" onClick={handleCreate}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="icon icon-tabler icon-tabler-plus"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  stroke="currentColor"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add ID Card
              </button>
            </div>
          </div>
        </div>

        {/* Development Status Banner */}
        <div
          className="alert alert-info alert-dismissible fade show"
          role="alert"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="icon icon-tabler icon-tabler-info-circle"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            strokeWidth="2"
            stroke="currentColor"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
            <circle cx="12" cy="12" r="9" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
            <polyline points="11,12 12,12 12,16 13,16" />
          </svg>
          <strong>Development Mode:</strong>{" "}
          {DEVELOPMENT_STATUS.DATA_SOURCE.DUMMY_DATA}.{" "}
          {DEVELOPMENT_STATUS.FEATURES.CRUD_OPERATIONS}.
          <button
            type="button"
            className="btn-close"
            data-bs-dismiss="alert"
            aria-label="Close"
          ></button>
        </div>

        {/* Search */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="row">
              <div className="col-md-6">
                <div className="input-icon">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search ID cards..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={handleSearch}
                  />
                  <span className="input-icon-addon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="icon icon-tabler icon-tabler-search"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      strokeWidth="2"
                      stroke="currentColor"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                      <circle cx="10" cy="10" r="7" />
                      <line x1="21" y1="21" x2="15" y2="15" />
                    </svg>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="card">
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-vcenter table-hover">
                <thead>
                  <tr>
                    <th>No</th>
                    <th>Action</th>
                    <th>Photo</th>
                    <th>Name</th>
                    <th>QR Code</th>
                    <th>Design</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="text-center">
                        <div
                          className="spinner-border spinner-border-sm"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        Loading ID cards...
                      </td>
                    </tr>
                  ) : idCardData?.data?.length > 0 ? (
                    idCardData.data.map((card, index) => (
                      <tr key={card.id}>
                        <td>
                          {(currentPage - 1) * (idCardData.per_page || 10) +
                            index +
                            1}
                        </td>
                        <td>
                          <Dropdown>
                            <Dropdown.Toggle
                              variant="default"
                              size="sm"
                              id={`dropdown-${card.id}`}
                            >
                              Actions
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              <Dropdown.Item
                                onClick={(e) => handleEdit(e, card)}
                              >
                                Edit
                              </Dropdown.Item>
                              <Dropdown.Item
                                onClick={() => handlePrint(card.id)}
                              >
                                Print
                              </Dropdown.Item>
                              <Dropdown.Item
                                onClick={() => handleDelete(card.id)}
                                className="text-danger"
                              >
                                Delete
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                        <td>
                          <span
                            className="avatar"
                            style={{
                              backgroundImage: `url(${
                                card.photo || "/default-avatar.png"
                              })`,
                            }}
                          ></span>
                        </td>
                        <td>{card.name}</td>
                        <td>
                          <code>{card.qrCode}</code>
                        </td>
                        <td>
                          <span className="badge bg-info">{card.design}</span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center">
                        No ID cards found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {idCardData?.pagination && (
              <div className="card-footer d-flex align-items-center">
                <div className="d-flex align-items-center me-3">
                  <span className="text-muted me-2">Show:</span>
                  <select
                    className="form-select form-select-sm"
                    value={perPage}
                    onChange={(e) => {
                      setPerPage(Number(e.target.value));
                      setCurrentPage(1); // Reset to first page when changing perPage
                    }}
                    style={{ width: "auto" }}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                  <span className="text-muted ms-2">entries</span>
                </div>
                <p className="m-0 text-muted">
                  Showing <span>{idCardData.pagination.from || 0}</span> to{" "}
                  <span>{idCardData.pagination.to || 0}</span> of{" "}
                  <span>{idCardData.pagination.total || 0}</span> entries
                </p>
                <ul className="pagination m-0 ms-auto">
                  <li
                    className={`page-item ${
                      !idCardData.pagination.prev_page_url ? "disabled" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={!idCardData.pagination.prev_page_url}
                    >
                      Previous
                    </button>
                  </li>
                  {Array.from(
                    { length: idCardData.pagination.last_page || 1 },
                    (_, i) => i + 1
                  ).map((page) => (
                    <li
                      key={page}
                      className={`page-item ${
                        page === currentPage ? "active" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </button>
                    </li>
                  ))}
                  <li
                    className={`page-item ${
                      !idCardData.pagination.next_page_url ? "disabled" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={!idCardData.pagination.next_page_url}
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </LayoutHR>
  );
};

export default IdcardPage;
